import { useEffect, useRef } from 'react';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  instagramLink: string;
  title: string;
}

export default function VideoModal({ isOpen, onClose, instagramLink, title }: VideoModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const extractInstagramPostId = (link: string): string | null => {
    const match = link.match(/\/p\/([A-Za-z0-9_-]+)/);
    return match ? match[1] : null;
  };

  const postId = extractInstagramPostId(instagramLink);
  const embedUrl = postId
    ? `https://www.instagram.com/p/${postId}/embed/`
    : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative bg-background-50 rounded-lg w-full max-w-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <h3 className="font-heading text-lg text-foreground-900">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors text-foreground-600"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
          </button>
        </div>

        <div className="relative bg-secondary-900 flex items-center justify-center" style={{ minHeight: '400px' }}>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full"
              style={{ height: '500px', border: 'none' }}
              scrolling="no"
              allowTransparency={true}
              title={title}
              onError={(e) => {
                const target = e.target as HTMLIFrameElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            ></iframe>
          ) : null}
          <div
            className={`${embedUrl ? 'hidden' : 'flex'} flex-col items-center justify-center gap-4 p-10 text-center`}
            style={embedUrl ? { display: 'none' } : {}}
          >
            <div className="w-16 h-16 rounded-full bg-background-50/10 flex items-center justify-center">
              <i className="ri-instagram-line text-2xl text-background-50/60 w-8 h-8 flex items-center justify-center"></i>
            </div>
            <p className="text-background-50/60 text-sm">
              Instagram videosu burada görüntülenecektir.
            </p>
            <a
              href={`https://www.instagram.com/p/${postId || ''}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-background-50/10 text-background-50 px-4 py-2 rounded-md text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
              Instagram'da Aç
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}